import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faImage, faSpinner, faTruck } from '@fortawesome/free-solid-svg-icons'
import PhotoGalleryFrame from '../PhotoGalleryFrame'
import type { FleetUnit } from './fleetTypes'
import { formatYearMakeModel } from './fleetTypes'

type FleetCardListProps = {
  items: FleetUnit[]
  loading: boolean
  error: string | null
  selectedId: string | null
  onSelect: (unit: FleetUnit) => void
}

const FleetCardThumb: React.FC<{ unit: FleetUnit }> = ({ unit }) => {
  const images = Array.isArray(unit.imageUrls) ? unit.imageUrls.filter(Boolean) : []
  const [index, setIndex] = useState(0)

  return (
    <PhotoGalleryFrame
      images={images}
      index={index}
      onIndexChange={setIndex}
      alt={unit.name}
      variant="wide"
      emptyLabel="No photo"
      className="max-h-36"
    />
  )
}

export const FleetCardList: React.FC<FleetCardListProps> = ({
  items,
  loading,
  error,
  selectedId,
  onSelect,
}) => {
  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <FontAwesomeIcon icon={faSpinner} className="text-3xl text-primary animate-spin" />
        <span className="ml-3 text-base-content/70">Loading fleet...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-box border border-error/30 bg-error/10 px-4 py-8 text-center text-error">
        {error}
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-box border border-dashed border-base-300 bg-base-200/40 px-4 py-16 text-center">
        <FontAwesomeIcon icon={faTruck} className="text-3xl text-base-content/35" />
        <div>
          <p className="font-medium">No fleet units found</p>
          <p className="text-sm text-base-content/60 mt-1">
            Sync from Linxup or add a vehicle to get started.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {items.map((unit) => {
        const selected = selectedId === unit._id
        const hasImage = Array.isArray(unit.imageUrls) && unit.imageUrls.some(Boolean)
        return (
          <div
            key={unit._id}
            role="button"
            tabIndex={0}
            className={`card bg-base-100 border text-left shadow-sm transition-all hover:shadow-md hover:border-primary/30 cursor-pointer ${
              selected ? 'border-primary ring-1 ring-primary/40' : 'border-base-200'
            }`}
            onClick={() => onSelect(unit)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onSelect(unit)
              }
            }}
          >
            <div className="card-body gap-3 p-4">
              {hasImage ? (
                <FleetCardThumb unit={unit} />
              ) : (
                <div className="flex h-28 w-full items-center justify-center rounded-box border border-base-300 bg-base-200 text-base-content/35">
                  <FontAwesomeIcon icon={faImage} className="text-3xl" />
                </div>
              )}
              <div className="min-w-0">
                <h4 className="card-title text-base leading-snug">{unit.name || 'Untitled'}</h4>
                <p className="text-sm text-base-content/70 mt-1">{formatYearMakeModel(unit)}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {unit.status ? (
                    <span className="badge badge-outline badge-sm capitalize">{unit.status}</span>
                  ) : null}
                  {unit.vin ? (
                    <span className="badge badge-ghost badge-sm font-mono">{unit.vin}</span>
                  ) : null}
                  {unit.externalSource === 'linxup' ? (
                    <span className="badge badge-success badge-outline badge-sm">Linxup</span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default FleetCardList
